"use client";

import type { ComponentType, CSSProperties } from "react";
import { useReducedMotion } from "@/lib/motion";
import { useCountUp } from "@/lib/hooks/use-count-up";

export interface StatItem {
  icon: ComponentType<{
    size?: number;
    style?: CSSProperties;
    "aria-hidden"?: "true" | "false" | boolean;
  }>;
  value: number;
  suffix: string;
  label: string;
  color: string;
  isFloat?: boolean;
}

interface StatCardProps {
  stat: StatItem;
  animate: boolean;
}

export function StatCard({ stat, animate }: StatCardProps) {
  const prefersReducedMotion = useReducedMotion();
  const shouldAnimate = animate && !prefersReducedMotion;
  /* Float stats count over whole numbers, so the run-up lands on the integer
     below the real figure (99 for 99.5). Snap to the true value on arrival —
     otherwise an animated "99.5%" stat renders as a permanent "99%". */
  const target = stat.isFloat ? Math.floor(stat.value) : stat.value;
  const count = useCountUp(target, 1800, shouldAnimate);
  const value = shouldAnimate && count < target ? count : stat.value;

  return (
    <div className="border-border bg-card/60 flex flex-col items-center gap-1.5 rounded-xl border px-6 py-4 backdrop-blur-sm">
      <stat.icon size={18} style={{ color: stat.color }} aria-hidden="true" />
      <div className="text-foreground text-2xl font-bold tabular-nums">
        {value}
        {stat.suffix}
      </div>
      <div className="text-muted-foreground text-xs font-medium">{stat.label}</div>
    </div>
  );
}
